import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

const EDGE_EPSILON = 1;

interface HorizontalOverflowState {
  overflow: boolean;
  left: boolean;
  right: boolean;
}

/** The two custom properties the shared edge-fade stylesheet reads: a fade of `--segmented-edge-fade-size`
 *  on each edge that hides content, none on the others. One owner for every swipeable track. */
export function horizontalEdgeFadeStyle(edges: Pick<HorizontalOverflowState, 'left' | 'right'>): CSSProperties {
  return {
    '--segmented-edge-fade-left': edges.left ? 'var(--segmented-edge-fade-size)' : '0px',
    '--segmented-edge-fade-right': edges.right ? 'var(--segmented-edge-fade-size)' : '0px',
  } as CSSProperties;
}

const NO_HORIZONTAL_OVERFLOW: HorizontalOverflowState = { overflow: false, left: false, right: false };

/** Measure which horizontal edges currently hide content. Shared by every swipeable one-line control so
 *  an edge fade reflects actual geometry rather than permanently decorating tracks that already fit.
 *
 *  The scroll range also covers the track's own padding, and padding scrolled out of view hides nothing. A
 *  strip whose items all fit could still scroll by its end padding, and it then faded a whole label at an
 *  end: a drawer's four header tabs on a 390px phone fit, yet the track scrolled 4px and faded "Obecné".
 *  So an edge counts only once the scroll position passes that edge's padding. */
function horizontalOverflowState(track: HTMLElement): HorizontalOverflowState {
  const style = getComputedStyle(track);
  const paddingLeft = Number.parseFloat(style.paddingLeft);
  const paddingRight = Number.parseFloat(style.paddingRight);
  if (Number.isNaN(paddingLeft) || Number.isNaN(paddingRight)) {
    throw new Error(`horizontal track has no computed padding: "${style.paddingLeft}" / "${style.paddingRight}"`);
  }
  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  const left = track.scrollLeft - paddingLeft > EDGE_EPSILON;
  const right = maxScrollLeft - track.scrollLeft - paddingRight > EDGE_EPSILON;
  return { overflow: left || right, left, right };
}

/** Keep a swipeable one-line track's edge state measured for as long as it is mounted: attach the returned
 *  ref to the track and publish `edges` as its fade state.
 *
 *  Everything that can change the answer is watched here once, so no caller has to remember all three: the
 *  box resizing, the track scrolling, and the CONTENT changing — a language change rewrites every label
 *  without touching a figure, and left the fade stating the old geometry until the mutations were observed
 *  too. Every observer is torn down with the component. `enabled=false` leaves the track unobserved
 *  and reports no edges; `onGeometryChange` can reveal an item before the new edges are measured.
 *  `measure` refreshes edges after a caller changes scrollLeft without a browser scroll event. */
export function useHorizontalOverflow<T extends HTMLElement>({ enabled = true, onGeometryChange }: {
  enabled?: boolean;
  onGeometryChange?: (track: T) => void;
} = {}): {
  ref: RefObject<T | null>;
  edges: HorizontalOverflowState;
  measure: () => void;
} {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<HorizontalOverflowState>(NO_HORIZONTAL_OVERFLOW);

  const measure = useCallback(() => {
    const track = ref.current;
    if (!track || !enabled) return;
    const next = horizontalOverflowState(track);
    setEdges((current) => (
      current.overflow === next.overflow && current.left === next.left && current.right === next.right
        ? current
        : next
    ));
  }, [enabled]);

  useEffect(() => {
    const track = ref.current;
    if (!track || !enabled) return;

    const geometryChanged = () => {
      onGeometryChange?.(track);
      measure();
    };
    const resizeObserver = new ResizeObserver(geometryChanged);
    const observeGeometry = () => {
      resizeObserver.observe(track);
      // A font can resize the items while the bounded track keeps exactly the same dimensions.
      for (const item of track.children) resizeObserver.observe(item);
      geometryChanged();
    };
    const mutationObserver = new MutationObserver(() => {
      resizeObserver.disconnect();
      observeGeometry();
    });
    observeGeometry();
    mutationObserver.observe(track, { characterData: true, childList: true, subtree: true });
    track.addEventListener('scroll', measure, { passive: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      track.removeEventListener('scroll', measure);
    };
  }, [enabled, measure, onGeometryChange]);

  return { ref, edges: enabled ? edges : NO_HORIZONTAL_OVERFLOW, measure };
}

/** How many of a row's leading items fit whole beside its fold chip. The row's children are the items, then
 *  the fold chip last, which carries "+N" for everything not shown. The largest count whose row fits wins;
 *  when none fits, one item stays, so a single long item truncates in place rather than folding into a
 *  bare "+N". Each item is read at its natural width, so the caller must not let a fitting item shrink. */
function fitCount(row: HTMLElement, itemCount: number, moreCount: number): number {
  if (itemCount === 0) return 0;
  const children = row.children;
  const available = row.clientWidth;
  const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
  const foldWidth = (children[itemCount] as HTMLElement).offsetWidth;
  let kept = 0;
  let width = 0;
  for (let count = 1; count <= itemCount; count += 1) {
    width += (children[count - 1] as HTMLElement).offsetWidth + (count > 1 ? gap : 0);
    const folded = itemCount - count + moreCount > 0;
    if (width + (folded ? gap + foldWidth : 0) <= available) kept = count;
  }
  return Math.max(1, kept);
}

/** Fit a one-line row: keep as many leading items whole as the row's width allows and fold the rest into
 *  its last child, the fold chip, which the caller renders after the items and labels with the count it
 *  folds (the hidden items plus `moreCount`, what the caller already folds beyond them).
 *
 *  Folded items stay in the DOM, so the hook can still read their natural widths: the caller renders them
 *  `invisible absolute`, which takes no room and no accessibility tree place. The row must be
 *  `flex-nowrap`, `relative` and `overflow-hidden`, and its items `shrink-0 max-w-full` (natural width,
 *  capped at the row), except the lone item when only one is shown, which may shrink so it truncates.
 *  The fold chip is `shrink-0`.
 *
 *  Measured on every render and on every resize of the row. A measurement that finds the same count sets
 *  nothing, so a render cannot loop. `enabled=false` leaves the row unobserved and shows every item. */
export function useHorizontalFold<T extends HTMLElement>({ itemCount, moreCount, enabled = true }: {
  itemCount: number;
  moreCount: number;
  enabled?: boolean;
}): {
  ref: RefObject<T | null>;
  shown: number;
} {
  const ref = useRef<T>(null);
  const [kept, setKept] = useState(itemCount);

  const measure = useCallback(() => {
    const row = ref.current;
    if (!row || !enabled) return;
    const next = fitCount(row, itemCount, moreCount);
    setKept((current) => (current === next ? current : next));
  }, [enabled, itemCount, moreCount]);

  // Labels and counts change as renders, so every render re-measures; a render that changes nothing does not
  // set state again.
  useLayoutEffect(() => { measure(); });

  useEffect(() => {
    const row = ref.current;
    if (!row || !enabled) return undefined;
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(row);
    return () => resizeObserver.disconnect();
  }, [enabled, measure]);

  return { ref, shown: enabled ? Math.min(kept, itemCount) : itemCount };
}

function wheelUnit(track: HTMLElement, deltaMode: number): number {
  if (deltaMode === WheelEvent.DOM_DELTA_PIXEL) return 1;
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return Math.max(1, track.clientHeight);

  const lineHeight = Number.parseFloat(getComputedStyle(track).lineHeight);
  return Number.isFinite(lineHeight) && lineHeight > 0
    ? lineHeight
    : Math.max(1, track.clientHeight);
}

/** Convert dominant vertical wheel input into bounded horizontal movement.
 *
 * Ctrl+wheel is reserved for browser zoom/pinch. The event is consumed only after scrollLeft actually
 * changes, so a fitting track and either bounded edge continue scrolling the page normally. */
export function consumeHorizontalWheel(track: HTMLElement, event: WheelEvent): boolean {
  if (event.ctrlKey || event.deltaY === 0 || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return false;

  const delta = event.deltaY * wheelUnit(track, event.deltaMode);
  if (!Number.isFinite(delta) || delta === 0) return false;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  const before = track.scrollLeft;
  const target = Math.max(0, Math.min(maxScrollLeft, before + delta));
  if (Math.abs(target - before) <= EDGE_EPSILON) return false;

  track.scrollLeft = target;
  if (Math.abs(track.scrollLeft - before) <= EDGE_EPSILON) return false;
  event.preventDefault();
  return true;
}

/** Keep one item visible on the horizontal axis without asking any ancestor page scroller to move.
 *
 *  The shortfall is measured from client rects, which are VISUAL pixels: every ancestor transform is
 *  already applied to them. `scrollLeft` is LAYOUT pixels. The two agree until something above the track
 *  is scaled — and a page overlay animates in with exactly that, so a strip revealing its section while
 *  the surface was still growing moved by a fraction of the distance it needed and stopped a few pixels
 *  short, with nothing later to correct it. `scale` converts one into the other, measured off the item
 *  itself in the same frame (`offsetWidth` is its layout width, the rect its visual one), so an ancestor's
 *  translation cancels out and its scale divides out. An environment that reports no layout box at all
 *  (jsdom) reads as "not scaled", which is what it is. */
export function revealHorizontalItem(track: HTMLElement, item: HTMLElement): boolean {
  const trackRect = track.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  let delta = 0;
  if (itemRect.left < trackRect.left) delta = itemRect.left - trackRect.left;
  else if (itemRect.right > trackRect.right) delta = itemRect.right - trackRect.right;
  if (Math.abs(delta) <= EDGE_EPSILON) return false;
  const scale = item.offsetWidth > 0 && itemRect.width > 0 ? item.offsetWidth / itemRect.width : 1;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  const before = track.scrollLeft;
  track.scrollLeft = Math.max(0, Math.min(maxScrollLeft, before + delta * scale));
  return Math.abs(track.scrollLeft - before) > EDGE_EPSILON;
}
